import clsx from 'clsx';
import { X } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { forwardRef, type ComponentProps, type ReactNode } from 'react';

type RootProps = ComponentProps<typeof PopoverPrimitive.Root>;

const HubertPopover = ({ children, ...props }: RootProps) => {
  return <PopoverPrimitive.Root {...props}>{children}</PopoverPrimitive.Root>;
};

interface TriggerProps extends ComponentProps<typeof PopoverPrimitive.Trigger> {
  className?: string;
  children: ReactNode;
}
function Trigger({ className, children, ...props }: TriggerProps) {
  return (
    <PopoverPrimitive.Trigger {...props} className={clsx(className)}>
      {children}
    </PopoverPrimitive.Trigger>
  );
}

interface CloseProps extends ComponentProps<typeof PopoverPrimitive.Close> {
  className?: string;
  children: ReactNode;
}
function Close({ className, children, ...props }: CloseProps) {
  return (
    <PopoverPrimitive.Close {...props} className={clsx(className)}>
      {children}
    </PopoverPrimitive.Close>
  );
}

interface ContentContainerProps extends ComponentProps<typeof PopoverPrimitive.Content> {
  closeButton?: boolean;
  includeArrow?: boolean;
  portalContent?: boolean;
  portalElement?: HTMLElement;
  className?: string;
  children: ReactNode;
}

function ContentContainer({
  className = '',
  closeButton = true,
  includeArrow = true,
  portalContent = false,
  portalElement,
  children,
  ...props
}: ContentContainerProps) {
  if (portalContent) {
    return (
      <PopoverPrimitive.Portal container={portalElement}>
        <Content className={className} closeButton={closeButton} includeArrow={includeArrow} {...props}>
          {children}
        </Content>
      </PopoverPrimitive.Portal>
    );
  }

  return (
    <Content className={className} closeButton={closeButton} includeArrow={includeArrow} {...props}>
      {children}
    </Content>
  );
}

interface ContentProps extends ComponentProps<typeof PopoverPrimitive.Content> {
  closeButton?: boolean;
  includeArrow?: boolean;
  className?: string;
  children: ReactNode;
}

const Content = forwardRef<HTMLDivElement, ContentProps>(function Content(
  { className = '', closeButton = true, includeArrow = true, children, ...props },
  ref,
) {
  return (
    <PopoverPrimitive.Content
      {...props}
      ref={ref}
      className={clsx(
        'bg-white p-2',
        'rounded-lg',
        'shadow-current',
        'shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)]',
        'will-change-[transform,opacity]',
        'z-10',
        'data-[state=open]:data-[side=top]:animate-slideDownAndFade',
        'data-[state=open]:data-[side=right]:animate-slideLeftAndFade',
        'data-[state=open]:data-[side=bottom]:animate-slideUpAndFade',
        'data-[state=open]:data-[side=left]:animate-slideRightAndFade',
        className,
      )}
    >
      {children}

      {closeButton && (
        <PopoverPrimitive.Close
          className={clsx(
            'absolute top-[5px] right-[5px]',
            'inline-flex items-center justify-center',
            'h-[16px] w-[16px]',
            'cursor-default outline-none',
            'rounded-full border-2 border-gray-200 hover:border-gray-300',
            'text-violet11 hover:bg-violet4 focus:shadow-violet7 focus:shadow-[0_0_0_1px]',
          )}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </PopoverPrimitive.Close>
      )}

      {includeArrow && <PopoverPrimitive.Arrow className="fill-white" />}
    </PopoverPrimitive.Content>
  );
});

HubertPopover.Trigger = Trigger;
HubertPopover.Content = ContentContainer;
HubertPopover.Close = Close;
export default HubertPopover;
